import {frontlineOptionName} from '../../../contracts/product-display-names-v1.ts';
export {frontlineOptionName};
import type {
  SmmCartComboIntent,
  SmmCartLine,
  SmmCartSelection,
  SmmCombo,
  SmmComboGroup,
  SmmComboPool,
  SmmMenuSnapshot,
  SmmOptionGroup,
  SmmProduct,
  SmmServiceMode,
} from './product-types';

export type SmmSelectionState=Readonly<Record<string,readonly string[]>>;
export type SmmComboSelectionState=Readonly<Record<string,readonly string[]>>;
export type SmmComboChildSelectionState=Readonly<Record<string,SmmSelectionState>>;

export interface SmmSelectionValidation {
  readonly ok:boolean;
  readonly issues:readonly string[];
}

export interface SmmResolvedComboChoice{
  readonly key:string;
  readonly poolId:string;
  readonly groupId:string;
  readonly subPoolId:string;
  readonly choiceId:string;
  readonly choiceType:'PRODUCT'|'LABEL'|'NONE';
  readonly choiceLabel:string;
  readonly productId?:string;
  readonly available:boolean;
  readonly publishedAdjustmentMinor:number;
  readonly optionGroups:readonly SmmOptionGroup[];
}

export interface SmmResolvedComboGroup{
  readonly pool:SmmComboPool;
  readonly group:SmmComboGroup;
  readonly key:string;
  readonly effectiveRequired:boolean;
  readonly effectiveMinSelections:number;
  readonly effectiveMaxSelections:number;
  readonly choices:readonly SmmResolvedComboChoice[];
}

export interface SmmResolvedCombo{
  readonly combo:SmmCombo;
  readonly groups:readonly SmmResolvedComboGroup[];
  readonly mainAdjustmentMinor:number;
}

/** UI initialization only: validators still require explicit facts in submitted carts. */
export function initialSmmSelections(groups:readonly SmmOptionGroup[]):SmmSelectionState{
  return Object.freeze(Object.fromEntries(groups.map(group=>[group.optionGroupId,Object.freeze(group.options.filter(option=>option.available&&option.defaultSelected).map(option=>option.optionId))])));
}

export function toggleSmmSelection(
  state:SmmSelectionState,
  group:SmmOptionGroup,
  optionId:string,
):SmmSelectionState{
  const current=state[group.optionGroupId]??[];
  const exists=current.includes(optionId);
  const next=exists
    ?current.filter(id=>id!==optionId)
    :group.maxSelections===1
      ?[optionId]
      :current.length<group.maxSelections?[...current,optionId]:current;
  return Object.freeze({...state,[group.optionGroupId]:Object.freeze(next)});
}

export function validateSmmSelections(product:SmmProduct,state:SmmSelectionState):SmmSelectionValidation{
  const issues:string[]=[];
  for(const group of product.optionGroups){
    const selected=state[group.optionGroupId]??[];
    const min=Math.max(group.required?1:0,group.minSelections);
    if(selected.length<min)issues.push(`${group.name}最少選擇 ${min} 項`);
    if(selected.length>group.maxSelections)issues.push(`${group.name}最多選擇 ${group.maxSelections} 項`);
    for(const optionId of selected){
      const option=group.options.find(candidate=>candidate.optionId===optionId);
      if(!option||!option.available)issues.push(`${group.name}包含不可用選項`);
    }
  }
  return Object.freeze({ok:issues.length===0,issues:Object.freeze(issues)});
}

export function selectedSmmCartOptions(product:SmmProduct,state:SmmSelectionState):readonly SmmCartSelection[]{
  return Object.freeze(product.optionGroups.flatMap(group=>
    (state[group.optionGroupId]??[]).flatMap(optionId=>{
      const option=group.options.find(candidate=>candidate.optionId===optionId);
      return option?[Object.freeze({
        optionGroupId:group.optionGroupId,
        optionId:option.optionId,
        optionName:option.name,
        ...(Number.isSafeInteger(Number(option.publishedAdjustmentMinor))?{publishedAdjustmentMinor:Number(option.publishedAdjustmentMinor)}:{}),
      })]:[];
    })
  ));
}

export function smmComboGroupKey(poolId:string,groupId:string){
  return poolId+'::'+groupId;
}
export const smmComboChoiceKey=(choice:{poolId:string;groupId:string;subPoolId:string;choiceId:string;productId?:string})=>JSON.stringify([choice.poolId,choice.groupId,choice.subPoolId,choice.choiceId,choice.productId??'']);
export function findSmmComboChoice(group:SmmResolvedComboGroup,id:string){
  const matches=group.choices.filter(choice=>choice.key===id||choice.choiceId===id);
  return matches.length===1?matches[0]:undefined;
}

function comboMainPoolContainsProduct(combo:SmmCombo,product:SmmProduct,menu:SmmMenuSnapshot){
  if(!combo.mainPoolId)return false;
  const pool=(menu.comboPools??[]).find(row=>row.poolId===combo.mainPoolId&&row.kind==='MAIN_COURSE');
  if(!pool)return false;
  return pool.groups.slice(0,1).some(group=>group.subPools.some(subPool=>subPool.choices.some(choice=>
    choice.choiceType==='PRODUCT'&&choice.productId===product.productId&&choice.available
  )));
}

export function resolveSmmProductCombo(product:SmmProduct,menu:SmmMenuSnapshot|undefined,serviceMode:SmmServiceMode='TAKEAWAY'):SmmResolvedCombo|null{
  if(!menu||!product.comboId)return null;
  const published=(menu.combos??[]).find(row=>row.comboId===product.comboId);
  if(!published||!comboMainPoolContainsProduct(published,product,menu))return null;
  const combo=product.mainMeal?{...published,publishedBasePriceMinor:Number(
    serviceMode==='DINE_IN'?product.publishedDineInUnitPriceMinor:product.publishedTakeawayUnitPriceMinor
  )}:serviceMode==='DINE_IN'&&published.publishedDineInBasePriceMinor!==undefined?{...published,publishedBasePriceMinor:published.publishedDineInBasePriceMinor}:published;
  const poolById=new Map((menu.comboPools??[]).map(pool=>[pool.poolId,pool] as const));
  const groups:SmmResolvedComboGroup[]=[];
  const mainPool=poolById.get(combo.mainPoolId??'');
  const snackCount=combo.addonPoolIds.flatMap(id=>{const pool=poolById.get(id);return pool?.addonKind==='SNACK'?pool.groups:[];}).length;
  const drinkCount=combo.addonPoolIds.flatMap(id=>{const pool=poolById.get(id);return pool?.addonKind==='DRINK'?pool.groups:[];}).length;
  const generic=Boolean(mainPool&&(mainPool.groups.length!==1||snackCount!==1||drinkCount!==1||mainPool.groups.some(group=>group.subPools.some(sub=>(sub.publishedAdjustmentMinor??0)!==0||sub.choices.some(choice=>(choice.publishedAdjustmentMinor??0)!==0)))));
  const mainAdjustmentMinor=generic?mainPool!.groups[0].subPools.reduce((sum,sub)=>sum+sub.choices.filter(choice=>choice.choiceType==='PRODUCT'&&choice.productId===product.productId).reduce((n,choice)=>n+(sub.publishedAdjustmentMinor??0)+(choice.publishedAdjustmentMinor??0),0),0):0;
  for(const poolId of [...(generic&&mainPool?[mainPool.poolId]:[]),...combo.addonPoolIds]){
    const pool=poolById.get(poolId);
    if(!pool)continue;
    for(const group of poolId===mainPool?.poolId?pool.groups.slice(1):pool.groups){
      const effectiveRequired=pool.addonKind==='DRINK'?false:group.required;
      const effectiveMinSelections=pool.addonKind==='DRINK'?0:Math.max(effectiveRequired?1:0,group.minSelections);
      const effectiveMaxSelections=Math.max(effectiveMinSelections,group.maxSelections);
      const choices=group.subPools.flatMap(subPool=>subPool.choices.map(choice=>{
        const child=choice.choiceType==='PRODUCT'?menu.products.find(p=>p.productId===choice.productId):undefined;
        return Object.freeze({
        key:smmComboChoiceKey({poolId:pool.poolId,groupId:group.groupId,subPoolId:subPool.subPoolId,choiceId:choice.choiceId,productId:choice.productId}),
        poolId:pool.poolId,
        groupId:group.groupId,
        subPoolId:subPool.subPoolId,
        choiceId:choice.choiceId,
        choiceType:choice.choiceType,
        choiceLabel:choice.label,
        ...(choice.productId?{productId:choice.productId}:{}),
        available:choice.available&&(choice.choiceType!=='PRODUCT'||Boolean(child?.available&&!child.variationRequired)),
        publishedAdjustmentMinor:subPool.publishedAdjustmentMinor+choice.publishedAdjustmentMinor,
        optionGroups:choice.choiceType==='LABEL'?choice.optionGroups??[]:child?.optionGroups??[],
      });}));
      groups.push(Object.freeze({
        pool,
        group,
        key:smmComboGroupKey(pool.poolId,group.groupId),
        effectiveRequired,
        effectiveMinSelections,
        effectiveMaxSelections,
        choices:Object.freeze(choices),
      }));
    }
  }
  return Object.freeze({combo,groups:Object.freeze(groups),mainAdjustmentMinor});
}

/** Cards retain real main product identities; synthetic main-meal upgrades stay in their product editor. */
export function smmComboCards(menu:SmmMenuSnapshot|undefined):readonly {readonly combo:SmmCombo;readonly mains:readonly SmmProduct[]}[]{
  if(!menu)return Object.freeze([]);
  return Object.freeze((menu.combos??[]).flatMap(combo=>{
    if(!Number.isSafeInteger(combo.publishedBasePriceMinor)||combo.publishedBasePriceMinor<0)return [];
    const mains=menu.products.filter(product=>product.available&&!product.mainMeal&&comboMainPoolContainsProduct(combo,product,menu)).map(product=>product.comboId===combo.comboId?product:{...product,comboId:combo.comboId});
    return mains.length?[Object.freeze({combo,mains:Object.freeze(mains)})]:[];
  }));
}

/** A changed choice gets its own defaults, never a cached selection from another drink. */
export function changeSmmComboChoice(
  state:SmmComboSelectionState,
  children:SmmComboChildSelectionState,
  group:SmmResolvedComboGroup,
  choiceId:string,
):{readonly selections:SmmComboSelectionState;readonly children:SmmComboChildSelectionState}{
  const selections=toggleSmmComboSelection(state,group,choiceId);
  const active=(selections[group.key]??[]).flatMap(id=>{const choice=findSmmComboChoice(group,id);return choice?[choice]:[];});
  const oldKeys=new Set((state[group.key]??[]).flatMap(id=>{const choice=findSmmComboChoice(group,id);return choice?[choice.key]:[];}));
  const groupKeys=new Set(group.choices.map(choice=>choice.key));
  const next=Object.fromEntries(Object.entries(children).filter(([key])=>!groupKeys.has(key)));
  for(const choice of active)next[choice.key]=oldKeys.has(choice.key)&&children[choice.key]?children[choice.key]:initialSmmSelections(choice.optionGroups);
  return Object.freeze({selections,children:Object.freeze(next)});
}

/** Display only: use saved per-unit facts, without deriving an adjustment from any line price. */
export function smmCartDisplayDetails(line:Pick<SmmCartLine,'selections'|'combo'>&Partial<Pick<SmmCartLine,'productId'>>,menu?:SmmMenuSnapshot):readonly {readonly label:string;readonly publishedAdjustmentMinor?:number}[]{
  const detail=(label:string,minor:number|undefined)=>Object.freeze({label,
    ...(typeof minor==='number'&&Number.isSafeInteger(minor)&&minor>0?{publishedAdjustmentMinor:minor}:{}),
  });
  const label=(selected:SmmCartSelection,groups:readonly SmmOptionGroup[]|undefined)=>frontlineOptionName({name:selected.optionName,shortName:groups?.find(group=>group.optionGroupId===selected.optionGroupId)?.options.find(option=>option.optionId===selected.optionId)?.shortName});
  const own=menu?.products.find(product=>product.productId===line.productId)?.optionGroups;
  return Object.freeze([
    ...line.selections.map(item=>detail(label(item,own),item.publishedAdjustmentMinor)),
    ...(line.combo?.selections.flatMap(item=>{
      const choice=menu?.comboPools?.find(pool=>pool.poolId===item.poolId)?.groups.find(group=>group.groupId===item.groupId)?.subPools.find(pool=>pool.subPoolId===item.subPoolId)?.choices.find(choice=>choice.choiceId===item.choiceId);
      const groups=item.choiceType==='LABEL'?choice?.optionGroups:menu?.products.find(product=>product.productId===item.productId)?.optionGroups;
      return [detail(item.choiceLabel,item.publishedAdjustmentMinor),...(item.optionSelections??[]).map(option=>detail(item.choiceLabel+'／'+label(option,groups),option.publishedAdjustmentMinor))];
    })??[]),
  ]);
}

export function toggleSmmComboSelection(
  state:SmmComboSelectionState,
  group:SmmResolvedComboGroup,
  choiceId:string,
):SmmComboSelectionState{
  const current=state[group.key]??[];
  const target=findSmmComboChoice(group,choiceId);
  if(!target)return state;
  const exists=current.some(id=>findSmmComboChoice(group,id)?.key===target.key);
  const next=exists
    ?current.filter(id=>findSmmComboChoice(group,id)?.key!==target.key)
    :group.effectiveMaxSelections===1
      ?[choiceId]
      :current.length<group.effectiveMaxSelections?[...current,choiceId]:current;
  return Object.freeze({...state,[group.key]:Object.freeze(next)});
}

export function validateSmmComboSelections(
  product:SmmProduct,
  menu:SmmMenuSnapshot|undefined,
  enabled:boolean,
  state:SmmComboSelectionState,
  children:SmmComboChildSelectionState={},
):SmmSelectionValidation{
  if(!enabled)return Object.freeze({ok:true,issues:Object.freeze([])});
  const resolved=resolveSmmProductCombo(product,menu);
  if(!resolved)return Object.freeze({ok:false,issues:Object.freeze(['套餐資料未完整，請重新同步'])});
  const issues:string[]=[];
  const knownKeys=new Set(resolved.groups.map(group=>group.key));
  for(const key of Object.keys(state)){
    if((state[key]?.length??0)>0&&!knownKeys.has(key))issues.push('套餐選項已更新，請重新選擇');
  }
  for(const group of resolved.groups){
    const selected=state[group.key]??[];
    const unique=[...new Set(selected.map(id=>findSmmComboChoice(group,id)?.key??id))];
    if(unique.length!==selected.length)issues.push(`${group.group.name}有重複選項`);
    if(unique.length<group.effectiveMinSelections)issues.push(`${group.group.name}最少選擇 ${group.effectiveMinSelections} 項`);
    if(unique.length>group.effectiveMaxSelections)issues.push(`${group.group.name}最多選擇 ${group.effectiveMaxSelections} 項`);
    for(const choiceId of unique){
      const choice=findSmmComboChoice(group,choiceId);
      if(!choice||!choice.available)issues.push(`${group.group.name}包含不可用選項`);
      else if(choice.optionGroups.length){
        const validation=validateSmmSelections({optionGroups:choice.optionGroups} as SmmProduct,children[choice.key]??{});
        issues.push(...validation.issues.map(issue=>choice.choiceLabel+'／'+issue));
        const child=children[choice.key]??{};
        if(Object.entries(child).some(([id,ids])=>ids.length&&!choice.optionGroups.some(g=>g.optionGroupId===id)))issues.push(choice.choiceLabel+'選項已更新');
        if(Object.values(child).some(ids=>new Set(ids).size!==ids.length)||choice.optionGroups.some(g=>(child[g.optionGroupId]??[]).some(id=>!Number.isSafeInteger(g.options.find(o=>o.optionId===id)?.publishedAdjustmentMinor))))issues.push(choice.choiceLabel+'選項資料無效');
      }
    }
  }
  return Object.freeze({ok:issues.length===0,issues:Object.freeze(issues)});
}

export function selectedSmmComboIntent(
  product:SmmProduct,
  menu:SmmMenuSnapshot|undefined,
  enabled:boolean,
  state:SmmComboSelectionState,
  serviceMode:SmmServiceMode='TAKEAWAY',
  children:SmmComboChildSelectionState={},
):SmmCartComboIntent|undefined{
  // An untouched main meal is an explicit PENDING recipe, never a standalone bypass.
  if(!enabled&&!product.mainMeal)return undefined;
  if(!enabled)state={};
  const validation=validateSmmComboSelections(product,menu,enabled,state,children);
  if(!validation.ok)return undefined;
  const resolved=resolveSmmProductCombo(product,menu,serviceMode);
  if(!resolved)return undefined;
  const selections=resolved.groups.flatMap(group=>{
    const selected=state[group.key]??[];
    return selected.flatMap(choiceId=>{
      const choice=findSmmComboChoice(group,choiceId);
      return choice?[Object.freeze({
        poolId:choice.poolId,
        groupId:choice.groupId,
        subPoolId:choice.subPoolId,
        choiceId:choice.choiceId,
        choiceType:choice.choiceType,
        choiceLabel:choice.choiceLabel,
        ...(choice.productId?{productId:choice.productId}:{}),
        publishedAdjustmentMinor:choice.publishedAdjustmentMinor,
        ...(choice.optionGroups.length?{optionSelections:Object.freeze(selectedSmmCartOptions({optionGroups:choice.optionGroups} as SmmProduct,children[choice.key]??{}).map(option=>Object.freeze({...option,publishedAdjustmentMinor:Number(option.publishedAdjustmentMinor)})))}:{}),
      })]:[];
    });
  });
  return Object.freeze({
    comboId:resolved.combo.comboId,
    comboName:resolved.combo.name,
    publishedBasePriceMinor:resolved.combo.publishedBasePriceMinor,
    selections:Object.freeze(selections),
  });
}

export function comboSelectionStateFromIntent(intent:SmmCartComboIntent):SmmComboSelectionState{
  const out:Record<string,string[]>={};
  for(const selection of intent.selections){
    const key=smmComboGroupKey(selection.poolId,selection.groupId);
    (out[key]??=[]).push(smmComboChoiceKey(selection));
  }
  return Object.freeze(Object.fromEntries(
    Object.entries(out).map(([key,value])=>[key,Object.freeze(value)])
  ));
}
export function comboChildSelectionStateFromIntent(intent:SmmCartComboIntent):SmmComboChildSelectionState{
  return Object.freeze(Object.fromEntries(intent.selections.filter(s=>s.optionSelections!==undefined).map(s=>{
    const groups:Record<string,string[]>={};
    for(const option of s.optionSelections??[])(groups[option.optionGroupId]??=[]).push(option.optionId);
    return [smmComboChoiceKey(s),Object.freeze(groups)];
  })));
}

export function revalidateSmmCartComboIntent(
  product:SmmProduct,
  menu:SmmMenuSnapshot|undefined,
  intent:SmmCartComboIntent,
  serviceMode:SmmServiceMode='TAKEAWAY',
):SmmCartComboIntent|null{
  const card=smmComboCards(menu).find(card=>card.combo.comboId===intent.comboId)?.mains.find(main=>main.productId===product.productId);
  if(product.comboId!==intent.comboId&&!card)return null;
  product=card??product;
  const state=comboSelectionStateFromIntent(intent);
  const rebuilt=selectedSmmComboIntent(product,menu,true,state,serviceMode,comboChildSelectionStateFromIntent(intent));
  if(!rebuilt||rebuilt.comboId!==intent.comboId)return null;
  if(rebuilt.selections.length!==intent.selections.length)return null;
  return rebuilt;
}

export function publishedSmmComboUnitMinor(intent:SmmCartComboIntent,mainOptionAdjustmentMinor:number,mainChoiceAdjustmentMinor=0){
  const total=intent.publishedBasePriceMinor
    +mainOptionAdjustmentMinor
    +mainChoiceAdjustmentMinor
    +intent.selections.reduce((sum,row)=>sum+row.publishedAdjustmentMinor+(row.optionSelections??[]).reduce((n,option)=>n+Number(option.publishedAdjustmentMinor??0),0),0);
  return Number.isSafeInteger(total)&&total>=0?total:null;
}
